// The account key on the Devices page (D41, D59, docs/07 "The account key"): the first linked
// browser chooses who holds it (this browser, or the platform), then the panel shows its
// fingerprint and mode, approves devices where it is held, restores it from the recovery
// file, or says that this browser waits to be approved. A managed key needs none of that: the
// platform signs the list when a device is approved. An unlinked browser sees it read-only.
import { CheckCircle } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { type Messages, useT } from "../i18n";
import { toast } from "../layout/toast";
import type { Proto } from "../proto/index";
import { AccountPanel, LossWarning, PanelFacts, PanelFingerprint, PanelNote, PanelPrimary } from "./AccountPanel";
import { putAccountKey } from "./api";
import { ChooseKeyMode } from "./ChooseKeyMode";
import type { AccountKey } from "./key";
import { approveDevices } from "./recipients";
import { RestoreAccountKey } from "./RestoreAccountKey";
import type { AccountStatus } from "./setup";
import { type AccountSummary, accountStatusQuery, accountSummary } from "./summary";

interface Props {
  proto: Proto | undefined;
  /** This browser's device id once it is linked; absent, the panel only shows the state. */
  deviceId?: string;
  className?: string;
}

function facts(t: Messages["account"]["card"], summary: AccountSummary): [string, string][] {
  return [
    [t.facts.heldBy, t.heldBy[summary.role]],
    [t.facts.mode, summary.mode ? t.mode[summary.mode] : "—"],
    [t.facts.recipients, summary.recipients ? t.recipientCount(summary.recipients.length) : "—"],
    [t.facts.deviceList, summary.version ? t.listVersion(summary.version) : t.noListYet],
  ];
}

export function AccountKeyCard({ proto, deviceId, className }: Props) {
  const t = useT().account.card;
  const queryClient = useQueryClient();
  const status = useQuery(accountStatusQuery());
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: accountStatusQuery().queryKey });
    void queryClient.invalidateQueries({ queryKey: ["devices"] });
  }, [queryClient]);
  const fail = (message: string) => toast(message, "warn");

  if (status.isPending) return <AccountPanel className={className}><PanelNote>{t.checking}</PanelNote></AccountPanel>;
  if (status.isError) {
    return <AccountPanel className={className}><PanelNote>{t.unavailable(status.error.message)}</PanelNote></AccountPanel>;
  }

  const summary = accountSummary(status.data, proto);
  const head = summary.fingerprint ? (
    <>
      <PanelFingerprint value={summary.fingerprint} testId="account-fingerprint" />
      <PanelFacts facts={facts(t, summary)} />
    </>
  ) : null;
  const linked = proto && deviceId;

  if (!linked) {
    return (
      <AccountPanel className={className}>
        {head}
        <PanelNote>
          {summary.role === "create" ? t.noKeyYet : summary.role === "managed" ? t.managedUnlinked : t.linkToManage}
        </PanelNote>
      </AccountPanel>
    );
  }

  switch (summary.role) {
    case "create":
      return (
        <AccountPanel className={className}>
          <ChooseKeyMode proto={proto} deviceId={deviceId} onDone={refresh} onError={fail} />
        </AccountPanel>
      );
    case "managed":
      return (
        <AccountPanel className={className} subtitle={t.managedSubtitle}>
          {head}
          <PanelNote>
            <span className="font-semibold text-white">{t.managedStrong}</span>
            {t.managedNote}
          </PanelNote>
        </AccountPanel>
      );
    case "holder":
      return (
        <AccountPanel className={className}>
          {head}
          {status.data.local ? <HolderActions proto={proto} status={status.data} keyHeld={status.data.local} onChanged={refresh} /> : null}
        </AccountPanel>
      );
    case "mismatch":
      return (
        <AccountPanel className={className}>
          {head}
          <PanelNote>
            <span className="font-semibold text-white">{t.mismatchStrong}</span>
            {t.mismatchNote}
          </PanelNote>
        </AccountPanel>
      );
    case "restore":
      return (
        <AccountPanel className={className} subtitle={t.waitingSubtitle}>
          {head}
          <PanelNote>{t.restoreNote}</PanelNote>
          <RestoreAccountKey onDone={refresh} onError={fail} />
        </AccountPanel>
      );
    case "waiting":
      return (
        <AccountPanel className={className}>
          <PanelFacts facts={facts(t, summary)} />
          <PanelNote>{t.waitingNote}</PanelNote>
        </AccountPanel>
      );
  }
}

interface HolderProps {
  proto: Proto;
  status: AccountStatus;
  keyHeld: AccountKey;
  onChanged: () => void;
}

function HolderActions({ proto, status, keyHeld, onChanged }: HolderProps) {
  const t = useT().account.card;
  const [busy, setBusy] = useState(false);
  const registered = !!status.server;

  // A setup interrupted between storing the key and registering it converges here.
  useEffect(() => {
    if (registered) return;
    putAccountKey(keyHeld.alg, keyHeld.publicKey).then(onChanged, (e: unknown) => toast(e instanceof Error ? e.message : String(e), "warn"));
  }, [registered, keyHeld, onChanged]);

  async function onApprove() {
    setBusy(true);
    try {
      const version = await approveDevices(proto, keyHeld);
      toast(t.listSigned(version));
      onChanged();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PanelNote>
        <span className="font-semibold text-white">{t.holderStrong}</span>
        {t.holderNote(<span className="font-mono text-white">ocs account pin</span>)}
      </PanelNote>
      <LossWarning />
      <div className="mt-5 flex flex-wrap gap-2.5">
        <PanelPrimary disabled={busy} onClick={() => void onApprove()}>
          <CheckCircle weight="bold" className="h-4 w-4" /> {busy ? t.signing : t.approve}
        </PanelPrimary>
      </div>
    </>
  );
}
